import axios from 'axios';
import { AnyAction, combineReducers } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { v4 as uuidv4 } from 'uuid';

import config from 'config';
import {
  ChatConversation,
  ChatConversationMessage,
  ChatConversationMessageParams,
  ChatConversationMode,
  ChatMessageRatingUpdateRequest,
  FeedbackRating,
} from 'models/chatConversation';
import { createAction } from 'ducks/actionHelpers';
import { loadNonReservationToken } from './tracking';
import { buildCookieString } from 'lib/util/buildCookieString';
import { ADD_NOTIFICATION } from './notification';
import { ChatMessageType } from 'models/chatConversation';

const conversationIdKey = 'conversation-id';

const getCompoundKey = (digitalMapId?: string): string =>
  digitalMapId ? `${conversationIdKey}-${digitalMapId}` : conversationIdKey;

export const loadConversationId = (digitalMapId?: string): string => {
  const compoundKey = getCompoundKey(digitalMapId);

  const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${compoundKey}=`));
  const cookieConversationId = cookie?.split('=')[1];
  if (cookieConversationId) {
    return cookieConversationId;
  }

  const conversationId = uuidv4();

  document.cookie = buildCookieString(compoundKey, conversationId, 90);

  return conversationId;
};

export const createNewConversationId = (digitalMapId?: string): string => {
  const compoundKey = getCompoundKey(digitalMapId);
  const conversationId = uuidv4();
  document.cookie = buildCookieString(compoundKey, conversationId, 90);

  return conversationId;
};

export const clearConversationId = (digitalMapId: string): void => {
  const compoundKey = getCompoundKey(digitalMapId);
  document.cookie = buildCookieString(compoundKey, '', -1);
};

// Actions
const FETCH_CHAT_CONVERSATIONS_REQUEST = 'FETCH_CHAT_CONVERSATIONS_REQUEST';
const FETCH_CHAT_CONVERSATIONS_SUCCESS = 'FETCH_CHAT_CONVERSATIONS_SUCCESS';
const FETCH_CHAT_CONVERSATIONS_FAILURE = 'FETCH_CHAT_CONVERSATIONS_FAILURE';

const FETCH_CHAT_CONVERSATION_REQUEST = 'FETCH_CHAT_CONVERSATION_REQUEST';
const FETCH_CHAT_CONVERSATION_SUCCESS = 'FETCH_CHAT_CONVERSATION_SUCCESS';
const FETCH_CHAT_CONVERSATION_FAILURE = 'FETCH_CHAT_CONVERSATION_FAILURE';

const CREATE_CHAT_CONVERSATION_MESSAGE_REQUEST = 'CREATE_CHAT_CONVERSATION_MESSAGE_REQUEST';
const CREATE_CHAT_CONVERSATION_MESSAGE_SUCCESS = 'CREATE_CHAT_CONVERSATION_MESSAGE_SUCCESS';
const CREATE_CHAT_CONVERSATION_MESSAGE_FAILURE = 'CREATE_CHAT_CONVERSATION_MESSAGE_FAILURE';

const UPDATE_MESSAGE_RATING_REQUEST = 'UPDATE_MESSAGE_RATING_REQUEST';
const UPDATE_MESSAGE_RATING_SUCCESS = 'UPDATE_MESSAGE_RATING_SUCCESS';
const UPDATE_MESSAGE_RATING_FAILURE = 'UPDATE_MESSAGE_RATING_FAILURE';

// Action creators
const fetchChatConversationsRequest = () => createAction(FETCH_CHAT_CONVERSATIONS_REQUEST);
const fetchChatConversationsSuccess = (payload: { conversations: ChatConversation[] }) =>
  createAction(FETCH_CHAT_CONVERSATIONS_SUCCESS, payload);
const fetchChatConversationsFailure = (payload: string) =>
  createAction(FETCH_CHAT_CONVERSATIONS_FAILURE, payload);

const fetchChatConversationRequest = (payload: string) =>
  createAction(FETCH_CHAT_CONVERSATION_REQUEST, payload);
const fetchChatConversationSuccess = (payload: ChatConversation) =>
  createAction(FETCH_CHAT_CONVERSATION_SUCCESS, payload);
const fetchChatConversationFailure = (payload: string) =>
  createAction(FETCH_CHAT_CONVERSATION_FAILURE, payload);

const createChatConversationMessageRequest = (payload: ChatConversationMessageParams) =>
  createAction(CREATE_CHAT_CONVERSATION_MESSAGE_REQUEST, payload);
const createChatConversationMessageSuccess = (payload: ChatConversationMessage) =>
  createAction(CREATE_CHAT_CONVERSATION_MESSAGE_SUCCESS, payload);
const createChatConversationMessageFailure = (payload: string) =>
  createAction(CREATE_CHAT_CONVERSATION_MESSAGE_FAILURE, payload);

const updateMessageRatingRequest = (payload: {
  id: string;
  ratingUpdate: ChatMessageRatingUpdateRequest;
}) => createAction(UPDATE_MESSAGE_RATING_REQUEST, payload);
const updateMessageRatingSuccess = (payload: ChatConversationMessage) =>
  createAction(UPDATE_MESSAGE_RATING_SUCCESS, payload);
const updateMessageRatingFailure = (payload: string) =>
  createAction(UPDATE_MESSAGE_RATING_FAILURE, payload);

// Async action creators
export const fetchChatConversations = (apiKey: string, contentLanguage: string) => (
  dispatch: ThunkDispatch<Record<string, unknown>, Record<string, unknown>, AnyAction>
): Promise<void> => {
  const trackingId = loadNonReservationToken();

  dispatch(fetchChatConversationsRequest());
  return axios
    .get(`${config.apiUrl}/chatconversations`, {
      headers: {
        'x-api-key': apiKey,
        'accept-language': contentLanguage,
        'x-track-id': trackingId,
      },
    })
    .then((response) => {
      dispatch(fetchChatConversationsSuccess(response.data));
    })
    .catch((err) => {
      dispatch(fetchChatConversationsFailure(err.message));
    });
};

export const fetchChatConversation = (
  apiKey: string,
  conversationId: string,
  contentLanguage: string
) => (
  dispatch: ThunkDispatch<Record<string, unknown>, Record<string, unknown>, AnyAction>
): Promise<void> => {
  const trackingId = loadNonReservationToken();

  dispatch(fetchChatConversationRequest(conversationId));
  return axios
    .get(`${config.apiUrl}/chatconversations/${conversationId}`, {
      headers: {
        'x-api-key': apiKey,
        'accept-language': contentLanguage,
        'x-track-id': trackingId,
      },
    })
    .then((response) => {
      dispatch(fetchChatConversationSuccess(response.data));
    })
    .catch((err) => {
      dispatch(fetchChatConversationFailure(err.message));
    });
};

export const createChatConversationMessage = (
  apiKey: string,
  req: ChatConversationMessageParams,
  contentLanguage: string
) => (
  dispatch: ThunkDispatch<Record<string, unknown>, Record<string, unknown>, AnyAction>
): Promise<void> => {
  const trackingId = loadNonReservationToken();

  dispatch(createChatConversationMessageRequest(req));
  return axios
    .post(`${config.apiUrl}/chatconversationmessages`, req, {
      headers: {
        'x-api-key': apiKey,
        'accept-language': contentLanguage,
        'x-track-id': trackingId,
      },
    })
    .then((response) => {
      dispatch(createChatConversationMessageSuccess(response.data));
    })
    .catch((err) => {
      dispatch(createChatConversationMessageFailure(err.message));
    });
};

export const updateMessageRating = (
  apiKey: string,
  messageId: string,
  feedbackRating: FeedbackRating
) => (
  dispatch: ThunkDispatch<Record<string, unknown>, Record<string, unknown>, AnyAction>
): Promise<void> => {
  const trackingId = loadNonReservationToken();
  const ratingUpdate: ChatMessageRatingUpdateRequest = {
    feedback_rating: feedbackRating,
  };

  dispatch(updateMessageRatingRequest({ id: messageId, ratingUpdate }));
  return axios
    .post(`${config.apiUrl}/chatconversationmessages/${messageId}/rating`, ratingUpdate, {
      headers: {
        'x-api-key': apiKey,
        'x-track-id': trackingId,
      },
    })
    .then((response) => {
      dispatch(updateMessageRatingSuccess(response.data));
    })
    .catch((err) => {
      dispatch(updateMessageRatingFailure(err.message));
    });
};

type Action =
  | ReturnType<typeof fetchChatConversationsRequest>
  | ReturnType<typeof fetchChatConversationsSuccess>
  | ReturnType<typeof fetchChatConversationsFailure>
  | ReturnType<typeof fetchChatConversationRequest>
  | ReturnType<typeof fetchChatConversationSuccess>
  | ReturnType<typeof fetchChatConversationFailure>
  | ReturnType<typeof createChatConversationMessageRequest>
  | ReturnType<typeof createChatConversationMessageSuccess>
  | ReturnType<typeof createChatConversationMessageFailure>
  | ReturnType<typeof updateMessageRatingRequest>
  | ReturnType<typeof updateMessageRatingSuccess>
  | ReturnType<typeof updateMessageRatingFailure>
  | { type: typeof ADD_NOTIFICATION; payload: { type: string; payload: Record<string, string> } };

// Reducers
const error = (state = '', action: Action) => {
  switch (action.type) {
    case FETCH_CHAT_CONVERSATIONS_FAILURE:
    case FETCH_CHAT_CONVERSATION_FAILURE:
    case CREATE_CHAT_CONVERSATION_MESSAGE_FAILURE:
    case UPDATE_MESSAGE_RATING_FAILURE:
      return action.payload;
    case FETCH_CHAT_CONVERSATIONS_REQUEST:
    case FETCH_CHAT_CONVERSATIONS_SUCCESS:
    case FETCH_CHAT_CONVERSATION_REQUEST:
    case FETCH_CHAT_CONVERSATION_SUCCESS:
    case CREATE_CHAT_CONVERSATION_MESSAGE_REQUEST:
    case CREATE_CHAT_CONVERSATION_MESSAGE_SUCCESS:
    case UPDATE_MESSAGE_RATING_REQUEST:
    case UPDATE_MESSAGE_RATING_SUCCESS:
      return '';
    default:
      return state;
  }
};

const awaitingReply = (state = false, action: Action) => {
  switch (action.type) {
    case FETCH_CHAT_CONVERSATIONS_REQUEST:
    case FETCH_CHAT_CONVERSATION_REQUEST:
    case CREATE_CHAT_CONVERSATION_MESSAGE_REQUEST:
      return true;
    case FETCH_CHAT_CONVERSATIONS_SUCCESS:
    case FETCH_CHAT_CONVERSATIONS_FAILURE:
    case FETCH_CHAT_CONVERSATION_SUCCESS:
    case FETCH_CHAT_CONVERSATION_FAILURE:
    case CREATE_CHAT_CONVERSATION_MESSAGE_FAILURE:
      return false;
    case ADD_NOTIFICATION:
      // Set inFlight to false when we receive an AI response notification
      if (
        action.payload.type === 'CHAT_CONVERSATION' &&
        (action.payload.payload.message_type === 'AI_RESPONSE' ||
          action.payload.payload.message_type === 'SYSTEM_MESSAGE')
      ) {
        return false;
      }
      return state;
    default:
      return state;
  }
};

// Helper function to sort conversations by updated date-time in descending order
const sortConversationsByUpdatedDate = (conversations: ChatConversation[]): ChatConversation[] => {
  return [...conversations].sort((a, b) => {
    return (
      new Date(b.updated_date_time_utc).getTime() - new Date(a.updated_date_time_utc).getTime()
    );
  });
};

const conversations = (state: ChatConversation[] = [], action: Action) => {
  switch (action.type) {
    case FETCH_CHAT_CONVERSATIONS_SUCCESS:
      return sortConversationsByUpdatedDate(action.payload.conversations || []);
    case FETCH_CHAT_CONVERSATION_SUCCESS:
      // Add or update the conversation in the list
      const existingIndex = state.findIndex((conv) => conv.id === action.payload.id);
      let updatedConversations: ChatConversation[];
      if (existingIndex >= 0) {
        updatedConversations = state.map((conv, index) =>
          index === existingIndex ? action.payload : conv
        );
      } else {
        updatedConversations = [...state, action.payload];
      }
      return sortConversationsByUpdatedDate(updatedConversations);
    case CREATE_CHAT_CONVERSATION_MESSAGE_SUCCESS:
      // Check if the conversation exists
      const conversationExists = state.some((conv) => conv.id === action.payload.conversation_id);

      if (conversationExists) {
        // Update the existing conversation with the new message
        const conversationsWithNewMessage = state.map((conversation) =>
          conversation.id === action.payload.conversation_id
            ? {
                ...conversation,
                messages: [...conversation.messages, action.payload],
                updated_date_time_utc: new Date().toISOString(),
              }
            : conversation
        );
        return sortConversationsByUpdatedDate(conversationsWithNewMessage);
      } else {
        // Create a new conversation with this message
        const newConversation: ChatConversation = {
          id: action.payload.conversation_id,
          supplier_id: action.payload.supplier_id,
          visitor_id: action.payload.visitor_id,
          mode: 'AUTO' as any, // Default mode for new conversations
          messages: [action.payload],
          created_date_time_utc: action.payload.created_date_time_utc,
          updated_date_time_utc: new Date().toISOString(),
        };
        return sortConversationsByUpdatedDate([...state, newConversation]);
      }
    case UPDATE_MESSAGE_RATING_SUCCESS:
      // Update the message rating in the conversation
      const conversationsWithUpdatedRating = state.map((conversation) => ({
        ...conversation,
        messages: conversation.messages.map((message) =>
          message.id === action.payload.id
            ? { ...message, feedback_rating: action.payload.feedback_rating }
            : message
        ),
      }));
      return sortConversationsByUpdatedDate(conversationsWithUpdatedRating);
    case ADD_NOTIFICATION:
      // Only handle CHAT_CONVERSATION notifications
      if (action.payload.type !== 'CHAT_CONVERSATION') {
        return state;
      }

      const notificationPayload = action.payload.payload;
      const conversationId = notificationPayload.conversation_id;

      const newMessage: ChatConversationMessage = {
        id: notificationPayload.message_id,
        conversation_id: conversationId,
        message_type: notificationPayload.message_type as ChatMessageType,
        content: notificationPayload.content,
        created_date_time_utc: notificationPayload.created_date_time_utc,
        supplier_id: '',
        visitor_id: '',
        sender_id: '',
      };

      const conversationsWithWebsocketMessage = state.map((conversation) =>
        conversation.id === conversationId
          ? {
              ...conversation,
              mode: (notificationPayload.mode || conversation.mode) as ChatConversationMode,
              messages: [...conversation.messages, newMessage],
              updated_date_time_utc: notificationPayload.created_date_time_utc,
            }
          : conversation
      );
      return sortConversationsByUpdatedDate(conversationsWithWebsocketMessage);
    default:
      return state;
  }
};

const lastCreatedMessage = (state: ChatConversationMessage | null = null, action: Action) => {
  switch (action.type) {
    case CREATE_CHAT_CONVERSATION_MESSAGE_SUCCESS:
      return action.payload;
    case CREATE_CHAT_CONVERSATION_MESSAGE_REQUEST:
    case CREATE_CHAT_CONVERSATION_MESSAGE_FAILURE:
      return null;
    default:
      return state;
  }
};

const inFlightMessage = (state = '', action: Action) => {
  switch (action.type) {
    case CREATE_CHAT_CONVERSATION_MESSAGE_REQUEST:
      return action.payload.content;
    case CREATE_CHAT_CONVERSATION_MESSAGE_SUCCESS:
    case CREATE_CHAT_CONVERSATION_MESSAGE_FAILURE:
      return '';
    default:
      return state;
  }
};

export interface ChatConversationsState {
  error: ReturnType<typeof error>;
  awaitingReply: ReturnType<typeof awaitingReply>;
  conversations: ReturnType<typeof conversations>;
  lastCreatedMessage: ReturnType<typeof lastCreatedMessage>;
  inFlightMessage: ReturnType<typeof inFlightMessage>;
}

export default combineReducers({
  error,
  awaitingReply,
  conversations,
  lastCreatedMessage,
  inFlightMessage,
});
